import MediaViewer from '@unifyapps/carbon/components/MediaViewer';
import type { MediaType } from '@unifyapps/carbon/components/MediaViewer/types';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { withSuspense as withBlockSuspense } from '@unifyapps/carbon/no-code/components/withSuspense';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { isEmptyBlock } from '@unifyapps/carbon/no-code/utils/block';
import type { ImageBlockStateType, ImageComponentType } from '@unifyapps/defs/blocks/Image/types';
import type { TooltipPlacement } from '@unifyapps/defs/blocks/Typography/types';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import useToggle from '@unifyapps/hooks/useToggle';
import { useAdaptFileUrl } from '@unifyapps/network/hooks/useAdaptFileUrl';
import { UA_ASSETS_PATH } from '@unifyapps/network/utils/PublicEnv/assetsCdn';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { clsx } from 'clsx';
import _set from 'lodash/set';
import _some from 'lodash/some';
import type { ReactElement, SyntheticEvent } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Image as BaseImage } from '../common/Image';
import EmptyState from './components/EmptyState';
import { getAdaptedSrcAndAlt, getRenderEmptyState } from './utils';

const FALLBACK_IMAGE_SRC = `${UA_ASSETS_PATH}/interface/migrated/2d-graphic-wallpaper-with-colorful-grainy-gradients-compressed.jpg`;

function WithTooltip({
  text,
  placement,
  width,
  children,
}: {
  text: string;
  placement: TooltipPlacement;
  width: string | { custom: string } | undefined;
  children: ReactElement;
}) {
  const { className: tooltipClassName, style: tooltipStyle } = useBlockAppearanceStyles({
    appearanceStyles: { width },
  });

  const slotProps = useMemo(
    () => ({
      title: {
        className: clsx('p-xxs whitespace-pre-line', tooltipClassName),
        style: tooltipStyle,
      },
    }),
    [tooltipClassName, tooltipStyle],
  );

  return (
    <Tooltip placement={placement} slotProps={slotProps} title={text}>
      {children}
    </Tooltip>
  );
}

function Image({
  attributesAndEventListeners,
  events,
  computedBlockState,
  updateBlockState,
  dndRef,
  block,
  component,
  registry,
}: BlockComponentProps<ImageComponentType, ImageBlockStateType, ImageBlockStateType>) {
  const { content, appearance } = computedBlockState;
  const mode = useInterfaceStore().hooks.useMode();
  const { addOns } = content;
  const { src: _src, alt } = getAdaptedSrcAndAlt(content);
  const renderEmptyState = getRenderEmptyState(content, mode);

  const adaptFileUrl = useAdaptFileUrl();

  // adapt the file url to ensure it works on public pages
  // urls containing /api/file need auth token and proper domain for public access
  const src = useMemo(() => {
    if (!_src) return _src;
    return adaptFileUrl(_src);
  }, [adaptFileUrl, _src]);

  const { isOpenInMediaViewer } = computedBlockState.state;

  const { emitEvent } = useBlockEvents(events, computedBlockState.id);

  const [hasImageError, { on: setHasImageError, off: resetHasImageError }] = useToggle(false);

  const BlockRenderer = registry.getBlockRenderer();
  const { slots } = component;
  const fallbackSlot = slots?.fallback;

  const isClickEventPresent = useMemo(
    () => _some(events, { eventType: EventTypeEnum.OnClick }),
    [events],
  );

  const onClick = useCallback(
    (e: SyntheticEvent) => {
      void emitEvent({ eventType: EventTypeEnum.OnClick, domEvent: e });
    },
    [emitEvent],
  );

  const onClose = useCallback(() => {
    updateBlockState((draft) => {
      _set(draft, 'state.isOpenInMediaViewer', false);
    });
  }, [updateBlockState]);

  const onImageError = useCallback(() => {
    setHasImageError();
  }, [setHasImageError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset hasImageError because <img> element doesn't automatically re-trigger the onError callback when you change the src prop if the component is already in an error state
  useEffect(() => {
    resetHasImageError();
  }, [src, resetHasImageError]);

  const { className, style } = useBlockAppearanceStyles({
    appearanceStyles: appearance?.styles,
  });

  const adaptedMedia = useMemo((): MediaType[] => {
    if (!src) {
      return [];
    }
    return [
      {
        url: src,
        id: src,
        mimeType: 'image',
      },
    ];
  }, [src]);

  const contentEl = (
    <BaseImage
      alt={alt}
      attributesAndEventListeners={attributesAndEventListeners}
      hasClickEvent={isClickEventPresent}
      objectFit={appearance?.objectFit}
      onClick={onClick}
      onError={onImageError}
      ref={isOpenInMediaViewer ? null : dndRef}
      src={src}
      styles={appearance?.styles}
    />
  );

  const mediaViewerEl = isOpenInMediaViewer ? (
    <MediaViewer medias={adaptedMedia} onClose={onClose} />
  ) : null;

  if (renderEmptyState) {
    return (
      <Stack className={className} ref={dndRef} style={style} {...attributesAndEventListeners}>
        <EmptyState
          block={block}
          type={content.data?.type}
          uploadProgess={content.data?.uploadDetails?.progress}
        />
      </Stack>
    );
  }
  if (hasImageError) {
    // propagate all the props except for .src and .onError, which are set for fallback
    // allows fallback image to behave identically in event handling/shape/styling
    return (
      <Stack className={className} ref={dndRef} style={style} {...attributesAndEventListeners}>
        {fallbackSlot?.blockId && !isEmptyBlock(fallbackSlot.blockId) ? (
          <BlockRenderer blockId={fallbackSlot.blockId} parentBlockId={computedBlockState.id} />
        ) : (
          <BaseImage
            alt={alt}
            attributesAndEventListeners={attributesAndEventListeners}
            hasClickEvent={isClickEventPresent}
            objectFit={appearance?.objectFit}
            onClick={onClick}
            // don't attach error handler on fallback, prevent loop
            ref={isOpenInMediaViewer ? null : dndRef}
            src={FALLBACK_IMAGE_SRC}
            styles={appearance?.styles}
          />
        )}
      </Stack>
    );
  }
  if (!addOns?.tooltip?.text) {
    return (
      <>
        {contentEl}
        {mediaViewerEl}
      </>
    );
  }

  return (
    <>
      <WithTooltip
        placement={addOns.tooltip.placement ?? 'top'}
        text={addOns.tooltip.text}
        width={addOns.tooltip.width}
      >
        {contentEl}
      </WithTooltip>
      {mediaViewerEl}
    </>
  );
}

export default withBlockSuspense(memo(Image));
